import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

import formatMoney from "lib/formatMoney";

import { useTheme } from "@mui/material/styles";

const columnDescriptions = {
  date: "The month of the escrow calculation",
  payIn:
    "Pay in is an offseting amount, which is equal to all expenses per month",
  payOut: "The total payouts for the month",
  homeInsurance: "Amount paid for insurance coverage of the dwelling",
  mortgageInsurance: "Amount paid for mortgage insurance on high LTV loans",
  countyTax: "Tax paid to the county",
  schoolTax: "Tax paid for schools",
  floodInsurance: "Insurance for flood protection",
  noReserves:
    "This column shows the size of maximum drawdown if escrow account had no reserves and started from 0",
  withReserve:
    "Maximum drawdown from No reserve column is used as reserve amount. This column shows the payout schedule with initial resurves, ensuring no negative drawdowns",
  withCushion:
    "Many lenders require cushion for potential changes to policies. This column shows the payout schedule if reserves and initial cushion were the starting amounts in the escrow account. Formula shown is <reserve amount> + <cushion amount> = <starting amount>",
};

const EscrowExplanationTable = ({
  escrowResultsData,
  payoutDict,
  singleAnalysis = null,
}) => {
  const columns = [
    { id: "date", label: "Month", alwaysVisible: true },
    { id: "payIn", label: "Pay in", alwaysVisible: true },
    { id: "payOut", label: "Payouts", alwaysVisible: true },
    {
      id: "homeInsurance",
      label: "Home insurance",
      alwaysVisible: singleAnalysis,
    },
    {
      id: "mortgageInsurance",
      label: "Mortgage insurance",
      alwaysVisible: singleAnalysis,
    },
    { id: "countyTax", label: "County tax", alwaysVisible: singleAnalysis },
    { id: "cityTax", label: "City tax", alwaysVisible: singleAnalysis },
    { id: "schoolTax", label: "School tax", alwaysVisible: singleAnalysis },
    {
      id: "floodInsurance",
      label: "Flood insurance",
      alwaysVisible: singleAnalysis,
    },
    { id: "noReserves", label: "No reserves", alwaysVisible: true },
    { id: "withReserve", label: "With reserve", alwaysVisible: true },
    { id: "withCushion", label: "With cushion", alwaysVisible: true },
  ].filter((col) => {
    if (singleAnalysis) {
      if (Object.keys(payoutDict).includes(col.id)) {
        return singleAnalysis === col.id;
      } else {
        if (col.id === "payOut") {
          return false;
        }
        return true;
      }
    } else {
      return true;
    }
  });

  const theme = useTheme();

  const [visibleColumns, setVisibleColumns] = useState(
    columns.reduce((acc, col) => ({ ...acc, [col.id]: col.alwaysVisible }), {})
  );
  const [showPayouts, setShowPayouts] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentColumn, setCurrentColumn] = useState("");

  const handleTogglePayouts = () => {
    const updatedVisibility = { ...visibleColumns };
    columns.forEach((col) => {
      if (!col.alwaysVisible) {
        updatedVisibility[col.id] = !showPayouts;
      }
    });
    setVisibleColumns(updatedVisibility);
    setShowPayouts(!showPayouts);
    setTimeout(() => {
      const tableContainer = document.querySelector(".MuiTableContainer-root");
      if (tableContainer) {
        tableContainer.scrollLeft = tableContainer.scrollWidth;
      }
    }, 100); // slight delay of 100ms
  };

  const handlePopoverOpen = (event, columnId) => {
    setAnchorEl(event.currentTarget);
    setCurrentColumn(columnId);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setCurrentColumn("");
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <Box
      sx={{
        width: "100%",
        padding: { xs: 0, sm: 2 }, // No padding on mobile, padding on larger screens
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          marginTop: 2,
          borderRadius: theme.spacing(0.25),
        }}
      >
        <Table
          sx={{
            border: `1px solid ${theme.palette.grey[400]}`, // Set border color to the same gray as TextInput
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.common.white,
              }}
            >
              {columns
                .filter((col) => {
                  return col.alwaysVisible || visibleColumns[col.id];
                })
                .map((col, colIndex) => (
                  <TableCell
                    key={col.id}
                    sx={{
                      fontWeight: col.alwaysVisible ? "bold" : "400",
                      color: col.alwaysVisible
                        ? theme.palette.common.black
                        : theme.palette.primary.main,
                      textAlign: col.label === "Payouts" ? "center" : "inherit",
                      "&:hover": {
                        cursor: "default",
                        boxShadow: `0px 2px 3px ${theme.palette.common.black}`,
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      {col.label === "Payouts" ? (
                        <Button
                          variant="contained"
                          onClick={handleTogglePayouts}
                        >
                          {showPayouts ? "Hide Payouts" : "Show Payouts"}
                        </Button>
                      ) : (
                        <Box sx={{ display: "flex" }}>
                          <Box sx={{ display: "flex" }}>{col.label}</Box>
                          <Box>
                            <Tooltip
                              title={columnDescriptions[col.id]}
                              arrow
                              placement="top"
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  marginLeft: 1,
                                  color: theme.palette.info.main,
                                  width: "100%",
                                  height: "100%",
                                  borderRadius: 0,
                                }}
                              >
                                <InfoIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {escrowResultsData.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  "&:hover": {
                    cursor: "default",
                    outline: `2px solid ${theme.palette.primary.dark}`,
                    color: `${theme.palette.common.black} !important`,
                  },
                }}
              >
                {columns.map(
                  (col, colIndex) =>
                    (col.alwaysVisible || visibleColumns[col.id]) && (
                      <TableCell
                        key={col.id}
                        sx={{
                          "&:hover": {
                            fontWeight: 600,
                            ...(row[col.id] &&
                              row[col.id] < 0 && {
                                color: theme.palette.secondary.light,
                              }),
                          },
                          color: "inherit",
                          ...(colIndex === 0 && {
                            borderRight: `2px solid ${theme.palette.grey[400]}`, // Set border color to the same gray as TextInput
                            padding: "12px 16px",
                            fontWeight: "bold",
                          }),
                          ...(index === 0 &&
                            col.id === "withReserve" && {
                              outline: `2px solid ${theme.palette.success.main}`,
                            }),

                          ...(index === 0 && {
                            fontWeight: 600,
                            color:
                              col.id === "noReserves" ||
                              (row[col.id] &&
                                row[col.id].toString().replace("$", "") < 0)
                                ? theme.palette.error.main
                                : theme.palette.success.main,
                          }),
                          ...(row[col.id] &&
                            row[col.id] < 0 && {
                              color: theme.palette.error.main,
                            }),
                          ...(row.maxDrawdown &&
                            col.id === "noReserves" && {
                              boxShadow: `0 -2px 0 ${theme.palette.error.main}, 0 2px 0 ${theme.palette.error.main}`,
                            }),
                          ...(row.maxDrawdown &&
                            col.id === "withReserve" && {
                              boxShadow: `0 -2px 0 ${theme.palette.success.main}, 0 2px 0 ${theme.palette.success.main}`,
                            }),
                          ...(row.maxDrawdown &&
                            col.id === "withCushion" && {
                              boxShadow: `0 -2px 0 ${theme.palette.success.main}, 0 2px 0 ${theme.palette.success.main}`,
                            }),
                        }}
                      >
                        {col.id === "withCushion" && index === 0 ? (
                          <Box>{`$${formatMoney(
                            row["withReserve"]
                          )} + $${formatMoney(
                            row["withCushion"] - row["withReserve"]
                          )} = $${formatMoney(row["withCushion"])}`}</Box> // special way to display withCushion initial amount (reserve + cushion)
                        ) : col.id === "date" ? (
                          row[col.id]
                        ) : row[col.id] || row[col.id] === 0 ? (
                          `$${formatMoney(row[col.id])}`
                        ) : null}
                      </TableCell>
                    )
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default EscrowExplanationTable;
